/**
 * Savings badge ($X OFF) for product cards and the PDP gallery.
 *
 * The amount is derived from the rendered price pair rather than from a
 * variant -> savings map. The card JS swaps [data-list-price] innerHTML on
 * swatch change and knows nothing about image overlays, so any precomputed map
 * would go stale the moment someone clicked from steel to gold. Reading the
 * two numbers that are actually on screen means the badge cannot disagree with
 * the price sitting under it, whatever caused the change.
 *
 * Liquid renders the badge with its initial value so there is no flash and it
 * is correct without JS; this only keeps it honest after a variant change.
 */
(function () {
  'use strict';

  var CARD_PRICE = '[data-list-price]';
  var CARD_BADGE = '[data-savings-badge]';
  var PDP_PRICE = '.product-hero .price--on-sale .price__sale';
  var PDP_BADGE = '[data-pdp-savings-badge]';

  function toNumber(el) {
    if (!el) return null;
    var digits = el.textContent.replace(/[^0-9.]/g, '');
    if (!digits) return null;
    var value = parseFloat(digits);
    return isNaN(value) ? null : value;
  }

  /* Whole dollars when the saving lands on one, matching money_without_trailing_zeros
     elsewhere on the page. $16.80 stays $16.80; $50.00 shows as $50. */
  function money(amount) {
    var rounded = Math.round(amount * 100) / 100;
    return '$' + (rounded % 1 === 0 ? String(rounded) : rounded.toFixed(2));
  }

  /* Idempotent on purpose. The PDP badge lives inside the element being
     observed, so writing unconditionally would retrigger the observer and
     loop. Only touch the DOM when the rendered value actually changes. */
  function render(badge, was, now) {
    if (!badge) return;
    if (was === null || now === null || was <= now) {
      if (!badge.hidden) badge.hidden = true;
      return;
    }
    var next = money(was - now) + ' off';
    if (badge.textContent !== next) badge.textContent = next;
    if (badge.hidden) badge.hidden = false;
  }

  function updateCard(priceEl) {
    var card = priceEl.closest('.product-card') || priceEl.closest('product-card');
    if (!card) return;
    var badge = card.querySelector(CARD_BADGE);
    if (!badge) return;
    /* After the reference-first reorder the markup is:
       {% if on sale %}<del>compare</del> {% endif %}<span>price</span> */
    var was = toNumber(priceEl.querySelector('del'));
    var now = toNumber(priceEl.querySelector('span'));
    render(badge, was, now);
  }

  /* Two badges exist on the PDP, one in the mobile hero gallery and one in the
     desktop grid, because those are separate galleries and each hides the other
     by breakpoint. Update both: only one is ever visible, and rendering to a
     display:none badge is harmless. */
  function updatePdp() {
    var priceEl = document.querySelector(PDP_PRICE);
    var badges = document.querySelectorAll(PDP_BADGE);
    if (!priceEl || !badges.length) return;
    var was = toNumber(priceEl.querySelector('s.price-item--regular'));
    var now = toNumber(priceEl.querySelector('.price-item--sale'));
    Array.prototype.forEach.call(badges, function (badge) {
      render(badge, was, now);
    });
  }

  function watch(el, handler) {
    handler();
    new MutationObserver(handler).observe(el, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  }

  function init() {
    document.querySelectorAll(CARD_PRICE).forEach(function (priceEl) {
      watch(priceEl, function () { updateCard(priceEl); });
    });

    /* Watch a container, not .price__sale itself: a finish change can re-render
       the whole price block, which would orphan an observer bound to it.
       updatePdp re-queries each time, and render() is a no-op when nothing
       changed, so watching a broad subtree is safe.

       Derive that container from the price element rather than reaching for
       .product-hero directly. The PDP carries TWO nodes with that class, a nav
       padding wrapper and the real hero container, and querySelector returns
       the wrapper, which contains no price. An observer bound to it never fires,
       which is exactly how the badge ended up frozen on the previous variant. */
    if (document.querySelector(PDP_BADGE)) {
      var pdpPrice = document.querySelector(PDP_PRICE);
      var pdpScope = (pdpPrice && pdpPrice.closest('.product-hero')) || document.body;
      watch(pdpScope, updatePdp);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Collection pages paginate and filter in new cards, so pick those up too. */
  document.addEventListener('DOMContentLoaded', function () {
    new MutationObserver(function (records) {
      records.forEach(function (record) {
        record.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          var prices = node.matches && node.matches(CARD_PRICE)
            ? [node]
            : (node.querySelectorAll ? node.querySelectorAll(CARD_PRICE) : []);
          Array.prototype.forEach.call(prices, function (priceEl) {
            watch(priceEl, function () { updateCard(priceEl); });
          });
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  });
})();
